<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { getImage, getImageSize } from "@/tools/image";
import { CanvasData, Config, Size, Text, FontData } from "@/types";
import { addImage, getImageData, updateText } from "@/tools/canvas";
import { getPoints } from "@/tools/main";
import { drawCells, drawPoints, initSvg } from "@/tools/svg";
import { useDebounceFn } from "@vueuse/core";
import { loadFontToDocument } from "@/tools/font";

const config = ref<Config>({
    resolution: 99.5,
    n: 15000,
    stainedGlass: false,
});

const size = ref<Size>({ width: 1200, height: 600 });

const fonts = ref<FontData[]>([
    {
        name: "Merriweather",
        url: "url(fonts/Merriweather/Merriweather-Italic-VariableFont_opsz,wdth,wght.ttf)",
        style: "normal",
        weight: 400,
    },
    {
        name: "Knewave",
        url: "url(fonts/Knewave/Knewave-Regular.ttf)",
        style: "normal",
        weight: 400,
    },
    {
        name: "Roboto",
        url: "url(fonts/Roboto/Roboto-VariableFont_wdth,wght.ttf)",
        style: "normal",
        weight: 400,
    },
]);

const text = ref<Text>({
    content: "Code that loves design",
    size: 250,
    font: fonts.value[0].name,
});

const draw = async (context: CanvasRenderingContext2D, svgEl: SVGElement) => {
    const canvasData: CanvasData = { context, size: size.value };
    updateText(context, text.value, size.value);
    setTimeout(() => {
        const imageData = getImageData(canvasData);
        const points = getPoints(canvasData, imageData, config.value);
        const { pointsGroup, cellsGroup } = initSvg(svgEl);

        //drawPoints(pointsGroup, points);
        drawCells(cellsGroup, points, canvasData, config.value);
    }, 500);
};

const canvas = ref<HTMLCanvasElement | null>(null);
const svg = ref<SVGElement | null>(null);

const update = () => {
    if (canvas.value && svg.value) {
        const context = canvas.value.getContext("2d");
        if (context) {
            draw(context, svg.value);
        }
    }
};

const debouncedFn = useDebounceFn(() => {
    update();
}, 200);

const loadFonts = async () => {
    for (const font of fonts.value) {
        await loadFontToDocument(font);
    }
};

const init = async () => {
    await loadFonts();
    if (canvas.value && svg.value) {
        const context = canvas.value.getContext("2d");
        if (context) {
            context.canvas.width = size.value.width;
            context.canvas.height = size.value.height;
            svg.value.setAttribute("width", size.value.width.toString());
            svg.value.setAttribute("height", size.value.height.toString());
            draw(context, svg.value);
        }
    }
};

watch(() => config.value, debouncedFn, { deep: true });
watch(() => text.value, debouncedFn, { deep: true });

onMounted(() => {
    init();
});
</script>

<template>
    <div class="App">
        <div class="App__tools">
            <textarea v-model="text.content" />

            <div class="font">
                <v-select
                    :items="fonts"
                    v-model="text.font"
                    label="Font"
                    item-title="name"
                />
            </div>

            <div class="slider-container">
                <div class="slider">
                    <v-slider
                        v-model="text.size"
                        :max="500"
                        :min="50"
                        :step="1"
                        thumb-label
                    />
                </div>
                Font size
            </div>

            <div class="slider-container">
                <div class="slider">
                    <v-slider
                        v-model="config.resolution"
                        :max="100"
                        :min="97"
                        :step="0.1"
                        thumb-label
                    />
                </div>
                Precision
            </div>

            <div class="slider-container">
                <div class="slider">
                    <v-slider
                        v-model="config.n"
                        :max="100000"
                        :min="1000"
                        :step="1000"
                        thumb-label
                    />
                </div>
                Number of points
            </div>

            <div>
                <input type="checkbox" v-model="config.stainedGlass" /> Stained
                glass
            </div>
        </div>

        <div class="App__main">
            <canvas ref="canvas" />
            <svg ref="svg" />
        </div>
    </div>
</template>

<style lang="scss">
.App {
    padding: 20px;

    &__tools {
        padding: 20px 20px 0 20px;

        textarea {
            width: 300px;
            height: 80px;
            background: #ddd;
            resize: none;
            padding: 10px;
            outline: none;
        }
    }

    &__main {
        padding: 20px;
        display: flex;
    }
}

canvas {
    border: 1px solid black;
    display: none;
}

.slider-container {
    display: flex;
    align-items: center;
    gap: 10px;
}

.font {
    width: 300px;
}

.slider {
    width: 300px;

    .v-input__details {
        display: none;
    }
}

svg {
    //border: 1px solid red;
}
</style>
